import engagebayBaseAPI from '../../api';
import { getSessionRefreshToken } from '../authUtil';
import { HOST_URI } from '../../constants';

/**
 * Shared token-refresh state used by ALL request layers — Axios (tokenApi, sessionApi)
 * and the global jQuery ajaxError handler in EngagebayRFW.tsx.
 *
 * Why a shared module: refresh tokens are single-use. If a tokenApi call and a
 * sessionApi call (or a jQuery call) both receive 401 at the same instant, they
 * must share a single isRefreshing flag so only ONE /refresh-token call is made.
 * The others queue behind it via failedQueue and retry once the refresh completes.
 *
 * Usage contract: import isRefreshing/failedQueue/processQueue/refreshToken here.
 * Never duplicate this state in individual API files.
 */

/** True while a /refresh-token call is in flight. Read via live ES module binding; mutate only via setIsRefreshing(). */
export let isRefreshing = false;

/** Queued resolve/reject callbacks from concurrent 401s waiting for the refresh to complete. */
export let failedQueue: Array<{ resolve: (v?: any) => void; reject: (e?: any) => void }> = [];

/**
 * Resolves or rejects all queued 401 callbacks after a refresh attempt completes.
 * Pass error=null + token on success; pass error + token=null on failure.
 */
export const processQueue = (error: unknown, token: string | null = null): void => {
    failedQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
    failedQueue = [];
};

/**
 * Calls POST /rest/api/login/refresh-token with the stored refresh token.
 * Returns the new {token, refreshToken} pair on success, null on failure.
 *
 * Why engagebayBaseAPI (not tokenApi or sessionApi): using either intercepted
 * instance would cause an infinite 401 loop. The base instance has no interceptors.
 */
export const refreshToken = async (): Promise<{ token: string; refreshToken: string } | null> => {
    const rt = getSessionRefreshToken();
    if (!rt) return null;
    try {
        const response = await engagebayBaseAPI.post('/rest/api/login/refresh-token', {
            refreshToken: rt,
        });
        return response?.data ?? null;
    } catch (e: unknown) {
        console.error('Token refresh failed:', e);
        return null;
    }
};

/** Mutates isRefreshing. Required because ES module let-exports cannot be assigned from outside the declaring module. */
export const setIsRefreshing = (v: boolean): void => {
    isRefreshing = v;
};

/**
 * EngageBay-owned host suffixes — any hostname that ends with one of these belongs to us
 * and should receive Authorization header injection and 401 token-refresh handling.
 *
 * Covers:
 *   - backends.engagebay.com, backends-qa.engagebay.com (ticketing microservice)
 *   - analytics.engagebay.com (analytics service)
 *   - ebkb-dot-accountbox-154605.ue.r.appspot.com (knowledge base on GAE)
 *   - {version}-dot-ebkb-dot-accountbox-154605.ue.r.appspot.com (versioned KB)
 */
const ENGAGEBAY_HOST_SUFFIXES = ['engagebay.com', 'appspot.com'];

/**
 * Returns true if a URL targets one of our own backend services.
 *
 * Why this exists: our 401/refresh interceptors must not fire for external service
 * calls (Sentry, Google, CDNs, etc.). A 429 from Sentry or a 401 from a third-party
 * jQuery plugin must never trigger a token refresh or session reload. All three
 * intercept layers (tokenApi, sessionApi, jQuery ajaxError) import this guard.
 *
 * Matching rules (evaluated in order, first match wins):
 *   1. No URL / undefined → treat as ours (safe default for relative-URL callers)
 *   2. Relative URLs ("/rest/api/...") → always ours
 *   3. Same-origin absolute URL → ours
 *   4. Starts with HOST_URI (http://localhost:8888 in dev) → ours
 *   5. hostname === "localhost" → ours (KB on :8080, ticketing on :8091 in dev)
 *   6. hostname ends with *.engagebay.com or *.appspot.com → our microservices
 *   7. Anything else (sentry.io, cloudfront.net, ...) → external, return false
 */
export const isOurBackend = (url: string | undefined): boolean => {
    if (!url) return true;
    if (!url.startsWith('http')) return true;
    if (url.startsWith(window.location.origin)) return true;
    if (HOST_URI && url.startsWith(HOST_URI)) return true;
    try {
        const { hostname } = new URL(url);
        if (hostname === 'localhost') return true;
        return ENGAGEBAY_HOST_SUFFIXES.some(
            suffix => hostname === suffix || hostname.endsWith('.' + suffix)
        );
    } catch {
        return false;
    }
};

// Expose on window so legacy Backbone/jQuery code (e.g. global_error_handler.js) can apply the
// same "is this our own backend?" rule as the React/Axios layers without importing ES modules.
// Same pattern as ebLogout/clearSession in authUtil.ts.
if (typeof window !== "undefined") {
    (window as any).ebIsOurBackend = isOurBackend;
}
